import { telegram } from './telegram';

export type LocationSource =
  | 'browser-initial'
  | 'browser-refined'
  | 'browser-watch'
  | 'telegram'
  | 'last-known';

export interface LocationFix {
  lat: number;
  lng: number;
  accuracy: number | null;
  timestamp: number;
  source: LocationSource;
}

export interface HighAccuracyOptions {
  desiredAccuracyMeters?: number;
  initialTimeoutMs?: number;
  refinementTimeoutMs?: number;
  allowLastKnownFallback?: boolean;
  maxLastKnownAgeMs?: number;
  allowTelegramFallback?: boolean;
}

export interface HighAccuracyResult {
  fix: LocationFix | null;
  fulfilled: boolean;
  usedFallback: boolean;
  reason?: string;
  attempts: LocationSource[];
}

let lastFix: LocationFix | null = null;

const DEFAULT_DESIRED_ACCURACY = 25;
const DEFAULT_INITIAL_TIMEOUT = 10000;
const DEFAULT_REFINEMENT_TIMEOUT = 5000;
const DEFAULT_MAX_LAST_KNOWN_AGE = 60000;

function toFix(position: GeolocationPosition, source: LocationSource): LocationFix {
  return {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracy:
      typeof position.coords.accuracy === 'number'
        ? Math.max(position.coords.accuracy, 0)
        : null,
    timestamp: position.timestamp,
    source,
  };
}

function recordFix(fix: LocationFix) {
  lastFix = fix;
}

export function getLastKnownFix(maxAgeMs: number = DEFAULT_MAX_LAST_KNOWN_AGE): LocationFix | null {
  if (!lastFix) {
    return null;
  }
  const age = Date.now() - lastFix.timestamp;
  if (age > maxAgeMs) {
    return null;
  }
  return lastFix;
}

async function attemptBrowserLocation(
  desiredAccuracy: number,
  initialTimeout: number,
  refinementTimeout: number
): Promise<HighAccuracyResult> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return {
      fix: null,
      fulfilled: false,
      usedFallback: false,
      reason: 'Geolocation API unavailable',
      attempts: [],
    };
  }

  return new Promise((resolve) => {
    let resolved = false;
    let watchId: number | null = null;
    let refinementTimer: number | null = null;
    let bestPosition: GeolocationPosition | null = null;
    let bestSource: LocationSource = 'browser-initial';

    const cleanup = () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
        watchId = null;
      }
      if (refinementTimer !== null) {
        window.clearTimeout(refinementTimer);
        refinementTimer = null;
      }
    };

    const finalize = (position: GeolocationPosition, source: LocationSource, reason?: string) => {
      if (resolved) {
        return;
      }
      resolved = true;
      cleanup();
      const fix = toFix(position, source);
      recordFix(fix);
      resolve({
        fix,
        fulfilled: (fix.accuracy ?? Number.POSITIVE_INFINITY) <= desiredAccuracy,
        usedFallback: false,
        reason,
        attempts:
          source === 'browser-refined' || source === 'browser-watch'
            ? ['browser-initial', source]
            : ['browser-initial'],
      });
    };

    const fail = (reason: string) => {
      if (resolved) {
        return;
      }
      resolved = true;
      cleanup();
      resolve({
        fix: null,
        fulfilled: false,
        usedFallback: false,
        reason,
        attempts: [],
      });
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        bestPosition = position;
        bestSource = 'browser-initial';

        const accuracy = position.coords.accuracy ?? Number.POSITIVE_INFINITY;
        if (accuracy <= desiredAccuracy) {
          finalize(position, 'browser-initial');
          return;
        }

        watchId = navigator.geolocation.watchPosition(
          (watchPosition) => {
            const watchAccuracy = watchPosition.coords.accuracy ?? Number.POSITIVE_INFINITY;
            const bestAccuracy = bestPosition?.coords.accuracy ?? Number.POSITIVE_INFINITY;

            if (!bestPosition || watchAccuracy < bestAccuracy) {
              bestPosition = watchPosition;
              bestSource = watchId === null ? 'browser-initial' : 'browser-refined';
            }

            if (watchAccuracy <= desiredAccuracy) {
              finalize(watchPosition, 'browser-refined');
            }
          },
          () => {
            // Ignore watch errors; rely on refinement timeout
          },
          {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: refinementTimeout,
          }
        );

        refinementTimer = window.setTimeout(() => {
          if (bestPosition) {
            finalize(bestPosition, bestSource, 'Refinement timed out');
          } else {
            fail('Refinement timed out');
          }
        }, refinementTimeout);
      },
      (error) => {
        fail(error.message || 'Geolocation error');
      },
      {
        enableHighAccuracy: true,
        timeout: initialTimeout,
        maximumAge: 0,
      }
    );
  });
}

async function attemptTelegramLocation(): Promise<{ fix: LocationFix | null; reason?: string }> {
  try {
    const position = await telegram.requestLocation();
    if (!position) {
      return { fix: null, reason: 'Telegram returned no location' };
    }
    const fix: LocationFix = {
      lat: position.latitude,
      lng: position.longitude,
      accuracy: null,
      timestamp: Date.now(),
      source: 'telegram',
    };
    recordFix(fix);
    return { fix };
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    return { fix: null, reason };
  }
}

export async function requestPreciseLocation(
  options: HighAccuracyOptions = {}
): Promise<HighAccuracyResult> {
  const desiredAccuracy = options.desiredAccuracyMeters ?? DEFAULT_DESIRED_ACCURACY;
  const initialTimeout = options.initialTimeoutMs ?? DEFAULT_INITIAL_TIMEOUT;
  const refinementTimeout = options.refinementTimeoutMs ?? DEFAULT_REFINEMENT_TIMEOUT;
  const allowTelegram = options.allowTelegramFallback ?? true;
  const allowLastKnown = options.allowLastKnownFallback ?? true;
  const maxLastKnownAge = options.maxLastKnownAgeMs ?? DEFAULT_MAX_LAST_KNOWN_AGE;

  const browserResult = await attemptBrowserLocation(
    desiredAccuracy,
    initialTimeout,
    refinementTimeout
  );

  if (browserResult.fix) {
    return browserResult;
  }

  let attempts: LocationSource[] = [...browserResult.attempts];
  let reason = browserResult.reason;

  if (allowTelegram) {
    const telegramAttempt = await attemptTelegramLocation();
    if (telegramAttempt.fix) {
      return {
        fix: telegramAttempt.fix,
        fulfilled: false,
        usedFallback: true,
        reason: 'Using Telegram-provided location. Accuracy unknown.',
        attempts: [...attempts, 'telegram'],
      };
    }
    reason = telegramAttempt.reason ?? reason;
    attempts = [...attempts, 'telegram'];
  }

  if (allowLastKnown) {
    const lastKnown = getLastKnownFix(maxLastKnownAge);
    if (lastKnown) {
      return {
        fix: lastKnown,
        fulfilled: (lastKnown.accuracy ?? Number.POSITIVE_INFINITY) <= desiredAccuracy,
        usedFallback: true,
        reason: reason ?? 'Using last known location',
        attempts: [...attempts, 'last-known'],
      };
    }
  }

  return {
    fix: null,
    fulfilled: false,
    usedFallback: false,
    reason: reason ?? 'Unable to acquire location',
    attempts,
  };
}

export function storeExternalFix(fix: LocationFix) {
  recordFix(fix);
}
